import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilIcon,
  CheckIcon,
  ArrowPathIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { analysisCategories } from '../../services/enrichmentService';

const STORAGE_KEY = 'variableExportNames';

// Get custom export names from localStorage
export const getVariableExportNames = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Save custom export names to localStorage
export const saveVariableExportNames = (names) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(names));
    return true;
  } catch (error) {
    console.error('Failed to save variable export names:', error);
    return false;
  }
};

// Auto-save variable aliases when variables are selected/added (only if not already customized)
// This ensures that variables from the Esri API get their friendly names saved for export
export const autoSaveVariableAliases = (variables) => {
  if (!variables || variables.length === 0) return;

  const currentNames = getVariableExportNames();
  let hasChanges = false;

  variables.forEach(v => {
    // Only auto-save if: variable has an alias, and we don't already have a custom name for it
    if (v.alias && v.id && !currentNames[v.id]) {
      currentNames[v.id] = v.alias;
      hasChanges = true;
    }
  });

  if (hasChanges) {
    saveVariableExportNames(currentNames);
    console.log('[VariableExportNames] Auto-saved aliases for', variables.length, 'variables');
  }
};

// Get a single variable's export name (custom or default)
export const getVariableExportName = (variableId, defaultLabel = null) => {
  const customNames = getVariableExportNames();
  if (customNames[variableId]) {
    return customNames[variableId];
  }
  // Fall back to default label or the variable ID
  return defaultLabel || variableId;
};

// Inline edit component for variable names
const VariableNameEditor = ({ variableId, defaultLabel, customName, onSave, onClear }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(customName || '');

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== defaultLabel) {
      onSave(variableId, trimmedValue);
      toast.success('Export name saved');
    } else if (!trimmedValue || trimmedValue === defaultLabel) {
      // Clear custom name if empty or same as default
      onClear(variableId);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(customName || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={defaultLabel}
          className="flex-1 rounded-md border border-blue-400 dark:border-blue-500
                   px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          autoFocus
        />
        <button
          onClick={handleSave}
          className="p-1 text-green-600 hover:text-green-700"
          title="Save"
        >
          <CheckIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            setEditValue(customName || '');
            setIsEditing(false);
          }}
          className="p-1 text-gray-500 hover:text-gray-700"
          title="Cancel"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className={`text-sm flex-1 ${customName ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-900 dark:text-white'}`}>
        {customName || defaultLabel}
        {customName && (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            (custom)
          </span>
        )}
      </span>
      <button
        onClick={() => {
          setEditValue(customName || defaultLabel);
          setIsEditing(true);
        }}
        className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Edit export name"
      >
        <PencilIcon className="h-4 w-4" />
      </button>
      {customName && (
        <button
          onClick={() => {
            onClear(variableId);
            toast.success('Custom name cleared');
          }}
          className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Clear custom name"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const VariableExportNamesEditor = () => {
  const [customNames, setCustomNames] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyCustomized, setShowOnlyCustomized] = useState(false);

  // Load custom names on mount
  useEffect(() => {
    setCustomNames(getVariableExportNames());
  }, []);

  // Get all variables from analysisCategories
  const allVariables = useMemo(() => {
    const vars = [];
    Object.entries(analysisCategories).forEach(([categoryKey, category]) => {
      category.variables.forEach(variable => {
        vars.push({
          id: variable.id,
          label: variable.label,
          category: category.label,
          categoryKey,
        });
      });
    });
    return vars;
  }, []);

  // Filter variables based on search and customized filter
  const filteredVariables = useMemo(() => {
    let filtered = allVariables;

    if (showOnlyCustomized) {
      filtered = filtered.filter(v => customNames[v.id]);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(v =>
        v.id.toLowerCase().includes(query) ||
        v.label.toLowerCase().includes(query) ||
        (customNames[v.id] && customNames[v.id].toLowerCase().includes(query)) ||
        v.category.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allVariables, searchQuery, showOnlyCustomized, customNames]);

  // Group filtered variables by category
  const groupedVariables = useMemo(() => {
    const groups = {};
    filteredVariables.forEach(v => {
      if (!groups[v.categoryKey]) {
        groups[v.categoryKey] = {
          label: v.category,
          variables: [],
        };
      }
      groups[v.categoryKey].variables.push(v);
    });
    return groups;
  }, [filteredVariables]);

  const handleSaveCustomName = (variableId, customName) => {
    const newNames = { ...customNames, [variableId]: customName };
    setCustomNames(newNames);
    saveVariableExportNames(newNames);
  };

  const handleClearCustomName = (variableId) => {
    const newNames = { ...customNames };
    delete newNames[variableId];
    setCustomNames(newNames);
    saveVariableExportNames(newNames);
  };

  const handleClearAllCustomNames = () => {
    if (window.confirm('Are you sure you want to clear all custom export names?')) {
      setCustomNames({});
      saveVariableExportNames({});
      toast.success('All custom export names cleared');
    }
  };

  const customizedCount = Object.keys(customNames).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-purple-500" />
            Variable Export Names
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Customize how variable names appear in Excel exports. Custom names override the default Esri labels.
          </p>
        </div>
        {customizedCount > 0 && (
          <button
            onClick={handleClearAllCustomNames}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600"
            title="Clear all custom names"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Clear All ({customizedCount})
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          {allVariables.length} total variables
        </span>
        <span className="text-purple-600 dark:text-purple-400 font-medium">
          {customizedCount} customized
        </span>
        {searchQuery && (
          <span className="text-blue-600 dark:text-blue-400">
            {filteredVariables.length} matching
          </span>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search variables by name, ID, or category..."
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 pl-10
                     shadow-sm px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyCustomized}
            onChange={(e) => setShowOnlyCustomized(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Show only customized
        </label>
      </div>

      {/* Variables List */}
      <div className="max-h-[calc(100vh-400px)] overflow-y-auto space-y-4">
        {Object.keys(groupedVariables).length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{showOnlyCustomized ? 'No customized variables' : 'No variables found'}</p>
          </div>
        ) : (
          Object.entries(groupedVariables).map(([categoryKey, category]) => (
            <div key={categoryKey} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {category.label}
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({category.variables.length} variable{category.variables.length !== 1 ? 's' : ''})
                  </span>
                </h4>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {category.variables.map(variable => (
                  <div key={variable.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          ID: {variable.id}
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                          Default: {variable.label}
                        </div>
                        <div className="font-medium">
                          <VariableNameEditor
                            variableId={variable.id}
                            defaultLabel={variable.label}
                            customName={customNames[variable.id]}
                            onSave={handleSaveCustomName}
                            onClear={handleClearCustomName}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VariableExportNamesEditor;
